import { Slot, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import { I18nextProvider } from 'react-i18next';

import { prefetchIndexLists } from '@/lib/detail/list-prefetch';
import { getInstanceForLocale } from '@/lib/i18n';
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isSupportedLocale } from '@todo/core/i18n/locales';
import { ogLocale, ogLocaleAlternates, organizationJsonLd } from '@todo/core/seo';

// Owns the locale for everything under app/[lang]/**: provides the
// locale-scoped i18next instance (see src/lib/i18n/index.ts — never the
// mutable global one, since static export renders /en, /fr, /ru in one
// process) and bakes <html lang> + og:locale into every page in this subtree.
export default function LangLayout() {
  const { lang } = useLocalSearchParams<{ lang: string }>();
  const locale = isSupportedLocale(lang) ? lang : DEFAULT_LOCALE;
  const instance = getInstanceForLocale(locale);

  return (
    <I18nextProvider i18n={instance}>
      {/* `key` forces a fresh mount on locale change — without it, switching
          languages client-side (no full page reload) leaves the browser tab
          title/meta/html-lang stuck on the previous locale even though the
          page body re-renders correctly; Helmet's reconciliation across an
          in-place update doesn't reliably recommit these on this route
          pattern. */}
      <Head key={locale}>
        {/* react-helmet(-async)'s nested-<html> convention — the vendored
            expo-router/head Helmet wrapper reads attributes off this child,
            not off a prop on <Head> itself. */}
        <html lang={locale} />
        <meta property="og:locale" content={ogLocale(locale)} />
        {ogLocaleAlternates(locale).map((alt) => (
          <meta key={alt} property="og:locale:alternate" content={alt} />
        ))}
        {/* The site's Organization entity, in the page's language. */}
        <script type="application/ld+json">{JSON.stringify(organizationJsonLd(locale))}</script>
      </Head>
      <Slot />
    </I18nextProvider>
  );
}

// Async so the index-page lists are fetched before anything in this subtree
// renders. expo-router awaits generateStaticParams (it has to — the params are
// what it renders), and this is the only hook guaranteed to run for the static
// /[lang]/events and /[lang]/tours routes, which have no dynamic segment of
// their own to hang a generateStaticParams off.
export async function generateStaticParams(): Promise<{ lang: string }[]> {
  await prefetchIndexLists();
  return SUPPORTED_LOCALES.map((lang) => ({ lang }));
}
